/**
 * Live updates: one EventSource per tab. Events invalidate the queries that show the changed entity (and the
 * boards, throttled), so several bots and humans see each other's work within ~1 s.
 */
import type { QueryClient } from "@tanstack/react-query";
import { qk } from "../api/keys";
import { withSlot } from "./identity";

export type LiveEvent = {
  id: number;
  t: number;
  kind: string;
  entity: string | null;
  entity_id: number | null;
  actor: string | null;
  payload: unknown;
};

type Listener = (e: LiveEvent) => void;
const listeners = new Set<Listener>();

export function onLiveEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function dispatchLive(qc: QueryClient, e: LiveEvent, boardThrottle: { last: number }): void {
  if (e.entity && e.entity_id != null) qc.invalidateQueries({ queryKey: qk.entity(e.entity, e.entity_id) });
  if (e.kind === "notification") qc.invalidateQueries({ queryKey: qk.notifications });
  if (e.kind === "presence") qc.invalidateQueries({ queryKey: qk.presence });
  if (e.kind === "settings") qc.invalidateQueries({ queryKey: qk.settings });
  if (e.kind === "job" || e.kind === "progress") qc.invalidateQueries({ queryKey: ["jobs"] });
  const now = Date.now();
  if (now - boardThrottle.last > 1000) {
    boardThrottle.last = now;
    qc.invalidateQueries({ queryKey: ["board"] });
    qc.invalidateQueries({ queryKey: ["list"] });
  }
  for (const fn of listeners) fn(e);
}

/** The stream authenticates as this tab's identity (`?slot=`; EventSource cannot send headers). The Layout opens
 * it only once GET /api/me has succeeded (never on /login or /join, where a stream would race the login and get a
 * 401) and reopens it when the tab's identity changes. */
export function startLive(qc: QueryClient): () => void {
  if (typeof EventSource === "undefined") return () => undefined;
  const throttle = { last: 0 };
  const handler = (m: MessageEvent<string>) => {
    try {
      dispatchLive(qc, JSON.parse(m.data) as LiveEvent, throttle);
    } catch {
      /* malformed event: ignore */
    }
  };
  const open = () => {
    const es = new EventSource(withSlot("/api/events"));
    for (const kind of ["job", "step", "progress", "media", "presence", "notification", "settings", "entity"]) {
      es.addEventListener(kind, handler as EventListener);
    }
    es.onmessage = handler;
    return es;
  };
  const es = open();
  return () => es.close();
}
